'use client';

import { fetchGoodsList } from '@/api/product';
import {
  IGroupInfo,
  IProductListApiParams,
  IProductListApiResponse,
  IProductListItem,
} from '@/api/types/product';
import GoodsFilter from '@/app/goods-list/components/GoodsFilter';
import ProductList from '@/app/goods-list/components/ProductList';
import SortFilterBar from '@/app/goods-list/components/SortFilterBar';
import FaqPart from '@/app/goods-list/components/FaqPart';
import RelatedBlockLink from '@/app/goods-list/components/RelatedBlockLink';
import Popup from '@/components/Popup';
import { ResponsiveContext } from '@/components/Responsive/ResponsiveProvider';
import { useStore } from '@/providers/app-store-provider';
import appCommunicationManager from '@/utils/app-communication-manager';
import logger from '@/utils/client-logger';
import { EAppCommunicationFuncName } from '@/utils/enums/app-sdk';
import { DEFAULT_SORT_VALUE } from '@/utils/constants';
import { EPlatform } from '@/utils/enums/platform-enum';
import { getHtmlText, transformFilterParams } from '@/utils/goods-list-utils';
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { SunglassesSearchParams, SunglassesServerData } from './types';
import { DEFAULT_GROUP_ID, PAGE_SIZE } from './config';
import {
  getDefaultPcSecondaryLocation,
  getNavigationLocationByGroupId,
  useNavigation,
} from './hooks/useNavigation';
import { useSunglassesTracking } from './hooks/useSunglassesTracking';
import NavigationSection from './components/NavigationSection';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { versionCompare } from '@/utils/tools';
import ExpandableText from '@/components/ExpandableText';
import { useTranslations } from '@/i18n/use-translations';
import { buildSunglassesUrl, parseSunglassesFilter, parseSunglassesPage } from './query-state';
interface SunglassesClientPageProps {
  serverData: SunglassesServerData;
  searchParams?: SunglassesSearchParams;
}

interface SunglassesPageCache {
  querySignature: string;
  listData: IProductListItem[];
  noMoreShow: boolean;
  infiniteScrollPage: number;
  column: number;
  expandedSections: Record<string, boolean>;
  scrollY?: number;
}

export default function SunglassesClientPage({
  serverData,
  searchParams: serverSearchParams,
}: SunglassesClientPageProps) {
  const { deviceType } = useContext(ResponsiveContext);
  const isWeb = deviceType === EPlatform.WEB;
  const config = serverData.pageConfig;
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const currentSort = serverSearchParams?.sort || DEFAULT_SORT_VALUE;
  const currentPage = parseSunglassesPage(serverSearchParams?.page);
  const currentFilter = useMemo(
    () => parseSunglassesFilter(serverSearchParams?.filter),
    [serverSearchParams?.filter],
  );
  const currency = useStore((state) => state.currency);
  const setIsShowFooterNav = useStore((state) => state.setIsShowFooterNav);
  const setIsGlobalLoading = useStore((state) => state.setIsGlobalLoading);
  const getPageCache = useStore((state) => state.getPageCache);
  const updatePageCache = useStore((state) => state.updatePageCache);
  const querySignature = useMemo(() => searchParams.toString(), [searchParams]);
  const cacheKey = useMemo(
    () => `campaigns-sunglasses-sale@${currency.name}@${isWeb ? 'web' : 'wap'}`,
    [currency.name, isWeb],
  );
  const storedPageState = useMemo(
    () => getPageCache(cacheKey) as SunglassesPageCache | null,
    [cacheKey, getPageCache],
  );
  const cachedPageState = useMemo(
    () => (storedPageState?.querySignature === querySignature ? storedPageState : null),
    [querySignature, storedPageState],
  );
  const initialCachedPageStateRef = useRef(cachedPageState);
  const navigation = config.navigation[isWeb ? 'web' : 'wap'];
  const requestedGroupId = Number(serverSearchParams?.group_id);
  const selectedGroupLocation = getNavigationLocationByGroupId(
    navigation,
    Number.isFinite(requestedGroupId) ? requestedGroupId : undefined,
  );
  const selectedQueryGroupId = selectedGroupLocation ? requestedGroupId : null;

  // App webview 相关状态
  const [inAppH5, setInAppH5] = useState(false);
  const [isUserInfoReady, setIsUserInfoReady] = useState(false);
  const syncAppLoginToken = useStore((state) => state.syncAppLoginToken);

  // 商品数据状态
  const [listData, setListData] = useState<IProductListItem[]>(
    cachedPageState?.listData ?? serverData.products,
  );
  const [total, setTotal] = useState(serverData.total);
  const [allTotal, setAllTotal] = useState(serverData.allTotal);
  const [imageRule, setImageRule] = useState(serverData.imageRule);
  const [imageHoverRule, setImageHoverRule] = useState(serverData.hoverImageRule);
  const [noData, setNoData] = useState(serverData.products.length === 0);
  const [loading, setLoading] = useState(false);
  const [noMoreShow, setNoMoreShow] = useState(
    cachedPageState?.noMoreShow ?? serverData.allTotal <= currentPage * PAGE_SIZE,
  );

  // 分页 & 布局
  const [infiniteScrollPage, setInfiniteScrollPage] = useState(
    cachedPageState?.infiniteScrollPage ?? currentPage,
  );
  const [column, setColumn] = useState(cachedPageState?.column ?? 2);

  // 筛选弹窗仅保留未提交的临时值；已提交筛选由 URL query 控制
  const [tempFilter, setTempFilter] = useState<Record<string, unknown>>(currentFilter);
  const [showFilter, setShowFilter] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(
    cachedPageState?.expandedSections ?? {},
  );
  const [stickyNavVisible, setStickyNavVisible] = useState(false);
  const handleStickyNavChange = useCallback((visible: boolean) => {
    setStickyNavVisible(visible);
  }, []);

  const [showTryOn, setShowTryOn] = useState(true);
  // 检测是否在 APP 内
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const { userAgent } = navigator;
      const isInApp = userAgent.includes('nextmar-xms-app');
      setInAppH5(isInApp);
      setIsUserInfoReady(!isInApp);
      if (isInApp) {
        const version = searchParams.get('version') || '';
        const moreThan = version ? versionCompare(version, '3.2.55', '>') : false;
        setShowTryOn(moreThan);
      }
    }
  }, [searchParams]);

  // 使用 ref 跟踪是否已经初始化，避免重复调用
  const hasInitialized = useRef(false);

  const t = useTranslations('goodsList');

  // App 通信初始化
  useEffect(() => {
    if (inAppH5 && !hasInitialized.current) {
      const handleAppCommunication = async () => {
        try {
          hasInitialized.current = true;

          appCommunicationManager.registerCallback(
            EAppCommunicationFuncName.APP_LOGIN_SUCCESS,
            async (_token) => {
              logger.log('App登录token:', _token);
              const result = await syncAppLoginToken(_token);
              if (result === 'authenticated') {
                setTimeout(() => {
                  setIsUserInfoReady(true);
                }, 1000);
              } else {
                setIsUserInfoReady(true);
              }
            },
            {},
            'BackTokenInfo' as EAppCommunicationFuncName,
          );
        } catch (e) {
          logger.error('App通信初始化失败:', e);
          hasInitialized.current = false;
        }
      };

      handleAppCommunication();

      // 添加超时机制，防止 App 通信失败导致页面一直加载
      const timeout = setTimeout(() => {
        if (!isUserInfoReady) {
          logger.warn('App通信超时，将以游客状态继续渲染页面');
          setIsUserInfoReady(true);
        }
      }, 5000);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [inAppH5, isUserInfoReady, syncAppLoginToken]);

  // 组件卸载时清理 App 通信管理器
  useEffect(() => {
    return () => {
      if (inAppH5) {
        appCommunicationManager.cleanup();
      }
    };
  }, [inAppH5]);

  // 筛选总数
  const filterTotal = useMemo(() => {
    return Object.keys(currentFilter).reduce((acc, key) => {
      if (['temple', 'frame_width'].includes(key)) return acc;
      const value = currentFilter[key];
      if (Array.isArray(value)) return acc + value.length;
      return value ? acc + 1 : acc;
    }, 0);
  }, [currentFilter]);

  const scrollToBelowNav = useCallback(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const navigateWithQuery = useCallback(
    (updates: Parameters<typeof buildSunglassesUrl>[2]) => {
      const currentQueryString = searchParams.toString();
      const currentUrl = `${pathname}${currentQueryString ? `?${currentQueryString}` : ''}`;
      const nextUrl = buildSunglassesUrl(pathname, currentQueryString, updates);
      if (nextUrl === currentUrl) return;

      setIsGlobalLoading(true);
      scrollToBelowNav();
      router.push(nextUrl, { scroll: false });
    },
    [pathname, router, scrollToBelowNav, searchParams, setIsGlobalLoading],
  );

  // 导航状态管理
  const nav = useNavigation({
    navigation,
    defaultGroupId: DEFAULT_GROUP_ID,
    selectedGroupId: selectedQueryGroupId,
    onNavigationChange: (groupId) =>
      navigateWithQuery({
        groupId,
        sort: DEFAULT_SORT_VALUE,
        filter: {},
        page: 1,
      }),
  });

  // 埋点
  const { trackPageView, trackPageLeave, trackNavTabClick, trackProductClick, trackProductExp } =
    useSunglassesTracking();

  // 页面浏览/离开埋点
  useEffect(() => {
    trackPageView();
    return () => {
      trackPageLeave();
    };
  }, [trackPageView, trackPageLeave]);

  const selectedSecondaryIndex = isWeb
    ? nav.pcSelectedSecondary[nav.primaryIndex]
    : nav.secondaryIndex >= 0
      ? nav.secondaryIndex
      : null;
  const currentSecondary = useMemo(() => {
    if (selectedSecondaryIndex === null || selectedSecondaryIndex === undefined) {
      return nav.currentPrimary.secondaryNav.find((item) => item.isDefault);
    }
    return nav.currentPrimary.secondaryNav[selectedSecondaryIndex];
  }, [nav.currentPrimary.secondaryNav, selectedSecondaryIndex]);

  // 当前 tab 名称（用于商品埋点）
  const currentTabName = currentSecondary?.label || nav.currentPrimary.label;

  const currentProductPoolTitle = currentSecondary?.productPoolTitle || nav.currentPrimary.label;
  const currentProductPoolSubtitle = currentSecondary?.productPoolSubtitle;
  const pcSecondaryBanner =
    isWeb && currentSecondary && !currentSecondary.isDefault
      ? currentSecondary.bannerImage || nav.currentPrimary.bannerImage
      : undefined;
  const relatedBlockLinks = currentSecondary?.relatedBlockLinks || [];
  const canShowSeoContent = isWeb || noMoreShow;

  // 初始化筛选项展开状态
  const publicResourceData = useStore((state) => state.publicResourceData);
  useEffect(() => {
    const product_filter_new = publicResourceData?.config_names?.product_filter_new || [];
    if (product_filter_new.length > 0) {
      const initialState: Record<string, boolean> = {};
      product_filter_new.forEach((item) => {
        initialState[item.attribute_code] = true;
      });
      setExpandedSections((previous) =>
        Object.keys(previous).length > 0 ? previous : initialState,
      );
    }
  }, [publicResourceData]);

  // URL query 变化后接收新的服务端数据；详情页返回时才恢复同一 query 的长列表。
  const initialDataRef = useRef(serverData);
  useEffect(() => {
    if (initialDataRef.current === serverData) return;
    initialDataRef.current = serverData;

    const storedCache = getPageCache(cacheKey) as SunglassesPageCache | null;
    const queryCache = storedCache?.querySignature === querySignature ? storedCache : null;
    setListData(queryCache?.listData ?? serverData.products);
    setTotal(serverData.total);
    setAllTotal(serverData.allTotal);
    setImageRule(serverData.imageRule);
    setImageHoverRule(serverData.hoverImageRule);
    setNoData(serverData.products.length === 0);
    setNoMoreShow(queryCache?.noMoreShow ?? serverData.allTotal <= currentPage * PAGE_SIZE);
    setInfiniteScrollPage(queryCache?.infiniteScrollPage ?? currentPage);
    setTempFilter(currentFilter);
    setLoading(false);
    setIsGlobalLoading(false);
  }, [
    cacheKey,
    currentFilter,
    currentPage,
    getPageCache,
    querySignature,
    serverData,
    setIsGlobalLoading,
  ]);

  // 与 goods-list 一致：query 不重复开缓存槽，只缓存重建长列表、布局所需的数据。
  const activeQuerySignatureRef = useRef(querySignature);
  useEffect(() => {
    if (activeQuerySignatureRef.current !== querySignature) {
      activeQuerySignatureRef.current = querySignature;
      return;
    }

    updatePageCache(cacheKey, {
      querySignature,
      listData,
      noMoreShow,
      infiniteScrollPage,
      column,
      expandedSections,
    } satisfies Omit<SunglassesPageCache, 'scrollY'>);
  }, [
    cacheKey,
    querySignature,
    listData,
    noMoreShow,
    infiniteScrollPage,
    column,
    expandedSections,
    updatePageCache,
  ]);

  const saveScrollPosition = useCallback(() => {
    updatePageCache(cacheKey, { querySignature, scrollY: window.scrollY });
  }, [cacheKey, querySignature, updatePageCache]);

  useEffect(() => {
    window.addEventListener('pagehide', saveScrollPosition);
    return () => {
      saveScrollPosition();
      window.removeEventListener('pagehide', saveScrollPosition);
    };
  }, [saveScrollPosition]);

  useEffect(() => {
    const cachedScrollY = initialCachedPageStateRef.current?.scrollY;
    if (!cachedScrollY) return;

    let restoreFrame = 0;
    const layoutFrame = window.requestAnimationFrame(() => {
      restoreFrame = window.requestAnimationFrame(() => {
        window.scrollTo({ top: cachedScrollY, behavior: 'instant' });
      });
    });

    return () => {
      window.cancelAnimationFrame(layoutFrame);
      window.cancelAnimationFrame(restoreFrame);
    };
  }, []);

  // 排序变化
  const handleSortChange = useCallback(
    (sortValue: { value: string | number; sortName?: string }) => {
      const newSort = sortValue.value.toString();
      navigateWithQuery({ sort: newSort, page: 1 });
    },
    [navigateWithQuery],
  );

  // 筛选变化
  const handleFilterChange = useCallback(
    (newFilter: Record<string, unknown>) => {
      setTempFilter(newFilter);
      navigateWithQuery({ filter: newFilter, page: 1 });
    },
    [navigateWithQuery],
  );

  // 分页变化
  const handlePageChange = useCallback(
    (page: number) => {
      navigateWithQuery({ page });
    },
    [navigateWithQuery],
  );

  // 无限滚动（M 端）
  const handleReachBottom = useCallback(async () => {
    if (isWeb || loading || noMoreShow) return;

    const nextPage = infiniteScrollPage + 1;
    setInfiniteScrollPage(nextPage);
    setLoading(true);
    setIsShowFooterNav(false);

    try {
      let sortParam = currentSort;
      if (sortParam === '' || sortParam === DEFAULT_SORT_VALUE) sortParam = '';
      const transformedFilter = transformFilterParams(currentFilter);

      const apiParams: IProductListApiParams = {
        group_id: nav.currentGroupId,
        currency: currency?.name,
        limit: PAGE_SIZE,
        page: nextPage,
        order: sortParam,
        in_stock: 1,
        is_sale: 1,
        filter: transformedFilter,
        append_correlation: 1,
        show_adv: 1,
      };

      const response = (await fetchGoodsList(apiParams)) as unknown as {
        code: number;
        data: IProductListApiResponse;
      };
      if (response.code === 200 && response.data) {
        const newList = response.data.list || [];
        setListData((prev) => [...prev, ...newList]);
        const _noMoreShow = (response.data.all_total || 0) <= nextPage * PAGE_SIZE;
        setNoMoreShow(_noMoreShow);
      }
    } catch (error) {
      logger.error('Infinite scroll fetch failed:', error);
    } finally {
      setLoading(false);
    }
  }, [
    isWeb,
    loading,
    noMoreShow,
    infiniteScrollPage,
    nav.currentGroupId,
    currentSort,
    currentFilter,
    currency?.name,
    setIsShowFooterNav,
  ]);

  // 筛选弹窗
  const handleFilterClick = useCallback(() => {
    setTempFilter(currentFilter);
    setShowFilter(true);
  }, [currentFilter]);

  const handleFilterClose = useCallback(() => {
    setTempFilter(currentFilter);
    setShowFilter(false);
  }, [currentFilter]);

  const handleFilterApply = useCallback(() => {
    handleFilterChange(tempFilter);
    setShowFilter(false);
  }, [tempFilter, handleFilterChange]);

  const handleFilterValueChange = useCallback(
    (filterId: string, value: unknown, isPopup: boolean) => {
      if (isPopup) {
        setTempFilter((prev) => ({ ...prev, [filterId]: value }));
      } else {
        const newFilter = { ...(currentFilter || {}), [filterId]: value };
        handleFilterChange(newFilter);
      }
    },
    [currentFilter, handleFilterChange],
  );

  const toggleSection = useCallback((sectionId: string) => {
    setExpandedSections((prev) => ({ ...prev, [sectionId]: !prev[sectionId] }));
  }, []);

  const handleColumnChange = useCallback(
    (newColumn: number) => {
      setColumn(newColumn);
      if (listData.length > 0) scrollToBelowNav();
    },
    [scrollToBelowNav, listData],
  );

  // 渲染筛选组件
  const renderGoodsFilter = useCallback(
    (isPopup = false) => {
      const filterValues = isPopup ? tempFilter : currentFilter;
      return (
        <GoodsFilter
          values={filterValues}
          onValueChange={(filterId, value) => handleFilterValueChange(filterId, value, isPopup)}
          onClear={() => {
            if (isPopup) setTempFilter({});
            else handleFilterChange({});
          }}
          isWeb={isWeb}
          expandedSections={expandedSections}
          onToggleSection={toggleSection}
          isPopup={isPopup}
          {...(isPopup ? { onApply: handleFilterApply } : {})}
        />
      );
    },
    [
      tempFilter,
      currentFilter,
      handleFilterValueChange,
      handleFilterApply,
      handleFilterChange,
      isWeb,
      expandedSections,
      toggleSection,
    ],
  );

  // M 端无限滚动到底部时显示底部导航
  useEffect(() => {
    if (!isWeb && noMoreShow) setIsShowFooterNav(true);
  }, [noMoreShow, isWeb, setIsShowFooterNav]);

  const stickyNavTop = useMemo(() => {
    if (inAppH5) {
      return stickyNavVisible ? '!top-[25.6vw]' : '!top-0';
    }
    if (isWeb) {
      return stickyNavVisible ? '2xl:top-[164px] top-[204px]' : 'top-[100px]';
    }
    return stickyNavVisible ? 'top-[37.333vw]' : 'top-[11.733vw]';
  }, [stickyNavVisible, inAppH5, isWeb]);

  return (
    <div className="min-h-screen">
      {/* 导航区域 */}
      <NavigationSection
        isWeb={isWeb}
        deviceType={deviceType}
        navigation={config.navigation}
        onStickyNavChange={handleStickyNavChange}
        stickyNavVisible={stickyNavVisible}
        primaryIndex={nav.primaryIndex}
        pcSelectedSecondary={nav.pcSelectedSecondary}
        onPrimaryClick={(index) => {
          nav.selectPrimary(index);
          // M 端切换一级导航时，上报默认二级选项
          if (!isWeb) {
            const primary = config.navigation.wap[index];
            const defaultIdx = Math.max(
              0,
              primary?.secondaryNav?.findIndex((s) => s.isDefault) ?? 0,
            );
            const globalIdx = config.navigation.wap
              .slice(0, index)
              .reduce((acc, p) => acc + (p?.secondaryNav?.length || 0), defaultIdx);
            trackNavTabClick(primary?.secondaryNav[defaultIdx]?.label || primary?.label, globalIdx);
          }
        }}
        onSecondaryToggle={(pIndex, sIdx) => {
          const primary = config.navigation.web[pIndex];
          const currentSelected = nav.pcSelectedSecondary[pIndex];
          nav.toggleSecondary(pIndex, sIdx);
          const offset = config.navigation.web
            .slice(0, pIndex)
            .reduce((acc, p) => acc + (p?.secondaryNav?.length || 0), 0);
          // PC 再次点击取消选中 → 上报 ALL；选中新的 → 上报选中项
          if (currentSelected === sIdx) {
            const defaultLocation = getDefaultPcSecondaryLocation(config.navigation.web);
            const defaultOffset = config.navigation.web
              .slice(0, defaultLocation?.primaryIndex ?? 0)
              .reduce((acc, item) => acc + (item?.secondaryNav?.length || 0), 0);
            const targetNav =
              defaultLocation &&
              config.navigation.web[defaultLocation.primaryIndex]?.secondaryNav[
                defaultLocation.secondaryIndex
              ];
            trackNavTabClick(
              targetNav?.label,
              defaultOffset + (defaultLocation?.secondaryIndex ?? 0),
            );
          } else {
            trackNavTabClick(primary?.secondaryNav[sIdx]?.label || '', offset + sIdx);
          }
        }}
        selectedBannerImage={pcSecondaryBanner}
        secondaryIndex={nav.secondaryIndex}
        onSecondaryClick={(sIdx) => {
          const tabLabel = nav.currentPrimary.secondaryNav[sIdx]?.label || '';
          const offset = (isWeb ? config.navigation.web : config.navigation.wap)
            .slice(0, nav.primaryIndex)
            .reduce((acc, p) => acc + (p?.secondaryNav?.length || 0), 0);
          trackNavTabClick(tabLabel, offset + sIdx);
          nav.selectSecondary(sIdx);
        }}
      />
      {/* 主体区域：左侧筛选 + 右侧商品 */}
      <div className={`relative ${isWeb ? 'w-full min-h-screen flex pt-[30px]' : ''}`}>
        {/* PC 左侧筛选面板 */}
        {isWeb && (
          <div className="max-[920px]:hidden pt-[4px]">
            <div
              className={`w-[304px] left-0 z-[2] h-screen sticky transition-[top] duration-300 ease-in-out ${stickyNavTop}`}
            >
              <div
                className="h-full bg-white overflow-y-auto show-scrollbar-when-hover"
                style={{ overscrollBehavior: 'contain' }}
              >
                <p className="pt-[16px] text-20 leading-[24px] font-bb pb-24 pl-48">
                  {t('Filters')}
                </p>
                <div className="pl-48 pr-24 pb-[350px]">{renderGoodsFilter(false)}</div>
              </div>
            </div>
          </div>
        )}

        {/* 右侧内容区域 */}
        <div
          className={`relative ${isWeb ? 'min-h-screen w-[calc(100vw-304px)] max-[920px]:w-full' : 'w-full'}`}
        >
          <div className={isWeb ? 'px-40 pb-40' : ''}>
            {/* 标题/描述/相关链接（与 goods-list GroupBanner 的 banner-down-area 保持一致） */}
            {(currentProductPoolTitle ||
              currentProductPoolSubtitle ||
              relatedBlockLinks.length > 0) && (
              <div
                className={`banner-down-area ${
                  isWeb
                    ? 'pt-20 pb-[24px] border-b border-solid border-[#eeeeee]'
                    : 'pt-[18px] pb-[12px]'
                }`}
              >
                {currentProductPoolTitle && (
                  <h2
                    className={`banner-title font-bb text-[#282828] ${
                      isWeb ? 'text-32 leading-[38px]' : 'text-[16px] leading-[19px] px-[14px]'
                    }`}
                    dangerouslySetInnerHTML={{
                      __html: getHtmlText(currentProductPoolTitle),
                    }}
                  />
                )}

                {currentProductPoolSubtitle && (
                  <h3
                    className={`banner-desc-wrapper text-[#282828] ${
                      isWeb ? 'mt-[15px] max-w-[750px]' : 'mt-[6px] px-[14px]'
                    }`}
                  >
                    <ExpandableText
                      text={currentProductPoolSubtitle}
                      maxLines={1}
                      className={`${isWeb ? 'text-14 leading-[22px]' : 'text-12 leading-[18px]'} font-ir`}
                      isWeb={isWeb}
                      textClassName="max-w-[750px]"
                    />
                  </h3>
                )}
              </div>
            )}

            {/* 排序筛选栏 */}
            <SortFilterBar
              wrapperClassName={`transition-[top] duration-300 ease-in-out ${stickyNavTop} pt-[4px]`}
              currentSort={currentSort}
              onSortChange={handleSortChange}
              filterTotal={filterTotal}
              onFilterClick={handleFilterClick}
              showColumnControl={!isWeb}
              column={column}
              onColumnChange={handleColumnChange}
              isWeb={isWeb}
              total={total}
              groupInfo={{ filter_show: 1 }}
              onSortBtnClick={() => {}}
            />

            {/* 商品列表 */}
            <section className="sunglasses-list-section">
              <ProductList
                showTryOn={showTryOn}
                listData={listData}
                total={total}
                allTotal={allTotal}
                noData={noData}
                isWeb={isWeb}
                column={column}
                isAcc={false}
                imageRule={imageRule}
                imageHoverRule={imageHoverRule}
                infiniteScrollPage={infiniteScrollPage}
                limit={PAGE_SIZE}
                handleReachBottom={handleReachBottom}
                page={currentPage}
                handlePageChange={handlePageChange}
                currentFilter={currentFilter}
                handleProductClick={(index: number, item: any) => {
                  saveScrollPosition();
                  trackProductClick(item, index, '商品版位', currentTabName);
                }}
                handleProductInView={() => {}}
                handleProductValidInView={(index: number, item: any) => {
                  trackProductExp(item, index, '商品版位', currentTabName);
                }}
                loading={loading}
                SDProductListPageTryon_Click={() => {}}
                sdProductListPageADExp={() => {}}
                sdProductListPageADClick={() => {}}
              />
            </section>
            {isWeb && relatedBlockLinks.length > 0 && (
              <div className="text-center mt-[56px] pt-40 border-t border-solid border-[#eeeeee]">
                <div className="seo-block-link mb-40">
                  <div className="flex flex-wrap gap-20">
                    {relatedBlockLinks.map((item, index) => (
                      <RelatedBlockLink
                        key={index}
                        item={item}
                        index={index}
                        isWeb={true}
                        trackProductListPageRelatedGroupClick={() => {}}
                        trackProductListPageRelatedGroupExp={() => {}}
                      />
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 底部搜索词 — M 端专用，复用 goods-list RelatedBlockLink */}
      {!isWeb && noMoreShow && relatedBlockLinks.length > 0 && (
        <div className="text-center px-[14px]">
          <div className="seo-block-link">
            <div className="flex flex-wrap gap-[12px]">
              {relatedBlockLinks.map((item, index) => (
                <RelatedBlockLink
                  key={index}
                  item={item}
                  index={index}
                  isWeb={false}
                  trackProductListPageRelatedGroupClick={() => {}}
                  trackProductListPageRelatedGroupExp={() => {}}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      {/* FAQ 区块 — 复用 goods-list FaqPart */}
      {canShowSeoContent && currentSecondary?.h1 && currentSecondary?.faq?.length > 0 && (
        <FaqPart
          groupInfo={
            {
              h_one: currentSecondary?.h1 || nav.currentPrimary.label,
              faq_list: (currentSecondary?.faq || []).map((item) => ({
                question: item.question,
                answer: item.answer,
                level: 3,
              })),
            } as unknown as IGroupInfo
          }
          isWeb={isWeb}
        />
      )}

      {/* PC 端筛选弹窗 */}
      {isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="left"
          width="482px"
          round={false}
          title={t('Filters')}
          bodyClassName="!pt-0 !px-32"
          sx={{
            '& .popup-title': {
              textAlign: 'left',
              border: 'none',
              paddingInline: '32px !important',
              paddingBlock: '26px !important',
              lineHeight: '24px',
              '& .popup-title-text': { fontSize: '20px !important' },
            },
          }}
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}

      {/* M 端筛选弹窗 */}
      {!isWeb && (
        <Popup
          open={showFilter}
          onClose={handleFilterClose}
          position="right"
          width="335px"
          round={false}
          title={t('Filters')}
          bodyClassName="!pt-0"
        >
          {renderGoodsFilter(true)}
        </Popup>
      )}
    </div>
  );
}
